// client/src/components/AdminCatalogPage.js
import React, { useMemo, useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import { jsPDF } from 'jspdf';
import JsBarcode from 'jsbarcode';
import DOMPurify from 'dompurify';
import AdminNavbar from './AdminNavbar';
import { FaSearch } from 'react-icons/fa';
import '../CSS/Dashboard.css';

const PAGE_SIZE        = 6;
const MAX_IMG_DIM      = 400;   // même dimension visuelle qu'avant
const MAX_TITLE_LENGTH = 52;
const MAX_CSV_SIZE     = 2 * 1024 * 1024; // 2 Mo max par fichier

const I18N = {
  fr: {
    languageName: 'Français',
    flag: '🇫🇷',
    general: {
      generateCatalogTitle: 'Générer un catalogue',
      priceLabel: 'Tarif :',
      downloadPdf: 'Télécharger le PDF',
      pdfFileName: 'catalogue-selection.pdf',
      chooseCover: 'Choisir couverture',
      chooseBack: 'Choisir page de fin',
      coverChosen: 'Couverture sélectionnée',
      backChosen: 'Page de fin sélectionnée',
      clear: 'Retirer',
    },
    toggles: {
      showPrices: 'Afficher les prix',
      showQuantities: 'Afficher les quantités',
      showPCB: 'Afficher le PCB',
      showPricesTitle: 'Afficher ou masquer les prix',
      showQuantitiesTitle: 'Afficher ou masquer les quantités',
      showPCBTitle: 'Afficher ou masquer le PCB',
    },
    priceModes: {
      labels: {
        REGULIER: 'TARIF REGULIER',
        MAGASIN: 'TARIF MAGASIN',
        CHAINE: 'TARIF CHAINE',
        DISTRIBUTEUR: 'TARIF DISTRIBUTEUR',
      },
      customLabel: ({ discount }) => `Tarif personnalisé (${discount}%)`,
      customOption: 'Personnalisé…',
      fallback: 'TARIF',
      selectTitle: 'Choisir le type de tarif',
      customDiscountTitle: 'Pourcentage de remise personnalisé',
    },
    searchPlaceholder: 'Rechercher (titre, fournisseur, marque, catégorie, variante, code-barres...)',
    filters: {
      animalLabel: 'Filtrer par animal',
      categoryLabel: 'Filtrer par catégorie',
      brandLabel: 'Filtrer par marque',
      allAnimals: 'Tous',
      allCategories: 'Toutes',
      allBrands: 'Toutes',
    },
    sort: {
      label: 'Trier par',
      options: {
        Aucun: 'Aucun',
        Animaux: 'Animaux',
        Marques: 'Marques',
        'Catégories': 'Catégories',
      },
      buttonTitle: {
        disabled: 'Choisis un mode de tri pour définir l’ordre',
        enabled: 'Définir l’ordre personnalisé et exclusions',
      },
    },
    cards: {
      supplier: 'Fournisseur :',
      animal: 'Animal :',
      category: 'Catégorie :',
      pcb: 'PCB :',
      pricePurchase: 'Prix achat :',
      quantity: 'Qté :',
      barcode: 'Code-barres :',
    },
    pagination: {
      prev: '« Précédent',
      next: 'Suivant »',
      status: ({ current, total }) => `Page ${current} / ${total}`,
    },
    pdf: {
      vendor: 'Fournisseur :',
      animal: 'Animal :',
      category: 'Catégorie :',
      pcb: ({ value }) => (value ? `PCB : ${value}` : 'PCB : -'),
      quantity: 'Qté :',
      additionalVariants: ({ rest }) => `+${rest} autres variantes`,
    },
    modal: {
      title: ({ mode }) => `Ordre personnalisé — ${mode}`,
      instructions: 'Glisse-dépose pour réordonner. Clique sur – pour exclure, et sur + pour réintégrer.',
      included: 'Inclus',
      excluded: 'Exclus',
      emptyIncluded: 'Aucun élément inclus pour le moment.',
      emptyExcluded: 'Aucun élément exclu.',
      closeTitle: 'Fermer',
      excludeTitle: 'Exclure cet élément',
      includeTitle: 'Réintégrer cet élément',
      dragTitle: 'Glisser pour réordonner',
      cancel: 'Annuler',
      apply: 'Valider',
    },
    alerts: {
      fileTooLarge: 'Un des fichiers dépasse la taille maximale de 2 Mo.',
      csvError: 'Erreur lors de la lecture du CSV.',
      parseErrorLog: 'Erreur parsing CSV :',
    },
    fallback: {
      unknown: '—',
      unknownVendor: 'Inconnu',
    },
  },
  en: {
    languageName: 'English',
    flag: '🇬🇧',
    general: {
      generateCatalogTitle: 'Generate a catalog',
      priceLabel: 'Pricing:',
      downloadPdf: 'Download PDF',
      pdfFileName: 'catalog-selection.pdf',
      chooseCover: 'Choose cover page',
      chooseBack: 'Choose end page',
      coverChosen: 'Cover selected',
      backChosen: 'End page selected',
      clear: 'Remove',
    },
    toggles: {
      showPrices: 'Show prices',
      showQuantities: 'Show quantities',
      showPCB: 'Show PCB',
      showPricesTitle: 'Show or hide prices',
      showQuantitiesTitle: 'Show or hide quantities',
      showPCBTitle: 'Show or hide PCB',
    },
    priceModes: {
      labels: {
        REGULIER: 'RETAIL PRICE',
        MAGASIN: 'STORE PRICE',
        CHAINE: 'CHAIN PRICE',
        DISTRIBUTEUR: 'DISTRIBUTOR PRICE',
      },
      customLabel: ({ discount }) => `Custom price (${discount}%)`,
      customOption: 'Custom…',
      fallback: 'PRICE',
      selectTitle: 'Choose a pricing mode',
      customDiscountTitle: 'Custom discount percentage',
    },
    searchPlaceholder: 'Search (title, supplier, brand, category, variant, barcode...)',
    filters: {
      animalLabel: 'Filter by animal',
      categoryLabel: 'Filter by category',
      brandLabel: 'Filter by brand',
      allAnimals: 'All',
      allCategories: 'All',
      allBrands: 'All',
    },
    sort: {
      label: 'Sort by',
      options: {
        Aucun: 'None',
        Animaux: 'Animals',
        Marques: 'Brands',
        'Catégories': 'Categories',
      },
      buttonTitle: {
        disabled: 'Select a sort mode to set the order',
        enabled: 'Customize ordering and exclusions',
      },
    },
    cards: {
      supplier: 'Supplier:',
      animal: 'Animal:',
      category: 'Category:',
      pcb: 'PCB:',
      pricePurchase: 'Purchase price:',
      quantity: 'Qty:',
      barcode: 'Barcode:',
    },
    pagination: {
      prev: '« Previous',
      next: 'Next »',
      status: ({ current, total }) => `Page ${current} / ${total}`,
    },
    pdf: {
      vendor: 'Supplier:',
      animal: 'Animal:',
      category: 'Category:',
      pcb: ({ value }) => (value ? `PCB: ${value}` : 'PCB: -'),
      quantity: 'Qty:',
      additionalVariants: ({ rest }) => `+${rest} more variants`,
    },
    modal: {
      title: ({ mode }) => `Custom ordering — ${mode}`,
      instructions: 'Drag and drop to reorder. Click – to exclude, and + to include again.',
      included: 'Included',
      excluded: 'Excluded',
      emptyIncluded: 'No items included yet.',
      emptyExcluded: 'No items excluded.',
      closeTitle: 'Close',
      excludeTitle: 'Exclude this item',
      includeTitle: 'Include this item again',
      dragTitle: 'Drag to reorder',
      cancel: 'Cancel',
      apply: 'Apply',
    },
    alerts: {
      fileTooLarge: 'One of the files exceeds the 2 MB size limit.',
      csvError: 'Error while reading the CSV file.',
      parseErrorLog: 'CSV parsing error:',
    },
    fallback: {
      unknown: '—',
      unknownVendor: 'Unknown',
    },
  },
};

const AVAILABLE_LANGUAGES = ['fr', 'en'];

const TITLE_TRANSLATIONS = {
  en: [
    { pattern: /\bCollier(s)?\b/gi, replace: (_m, plural) => plural ? 'Collars' : 'Collar' },
    { pattern: /\bLaisse(s)?\b/gi, replace: (_m, plural) => plural ? 'Leashes' : 'Leash' },
    { pattern: /\bHarnais\b/gi, replace: 'Harness' },
    { pattern: /\bJouet(s)?\b/gi, replace: (_m, plural) => plural ? 'Toys' : 'Toy' },
    { pattern: /\bFriandise(s)?\b/gi, replace: (_m, plural) => plural ? 'Treats' : 'Treat' },
    { pattern: /\bAliment(s)?\b/gi, replace: (_m, plural) => plural ? 'Food' : 'Food' },
    { pattern: /\bChiot(s)?\b/gi, replace: (_m, plural) => plural ? 'Puppies' : 'Puppy' },
    { pattern: /\bChien(s)?\b/gi, replace: (_m, plural) => plural ? 'Dogs' : 'Dog' },
    { pattern: /\bChaton(s)?\b/gi, replace: (_m, plural) => plural ? 'Kittens' : 'Kitten' },
    { pattern: /\bChat(s)?\b/gi, replace: (_m, plural) => plural ? 'Cats' : 'Cat' },
    { pattern: /\bPour\b/gi, replace: 'for' },
    { pattern: /\bEt\b/gi, replace: 'and' },
    { pattern: /\bPetit\b/gi, replace: 'Small' },
    { pattern: /\bGrande?\b/gi, replace: (m) => (m.toLowerCase() === 'grande' ? 'Large' : 'Large') },
    { pattern: /\bMoyen\b/gi, replace: 'Medium' },
    { pattern: /\bNoir\b/gi, replace: 'Black' },
    { pattern: /\bBleu\b/gi, replace: 'Blue' },
    { pattern: /\bRouge\b/gi, replace: 'Red' },
    { pattern: /\bVert\b/gi, replace: 'Green' },
    { pattern: /\bJaune\b/gi, replace: 'Yellow' },
    { pattern: /\bOrange\b/gi, replace: 'Orange' },
    { pattern: /\bRose\b/gi, replace: 'Pink' },
    { pattern: /\bViolet\b/gi, replace: 'Purple' },
    { pattern: /\bGris\b/gi, replace: 'Grey' },
    { pattern: /\bBlanc\b/gi, replace: 'White' },
    { pattern: /\bPack\b/gi, replace: 'Pack' },
    { pattern: /\bBoîte\b/gi, replace: 'Box' },
    { pattern: /\bLot\b/gi, replace: 'Set' },
    { pattern: /\bEnsemble\b/gi, replace: 'Set' },
    { pattern: /\bRecharge\b/gi, replace: 'Refill' },
    { pattern: /\bGamme\b/gi, replace: 'Range' },
    { pattern: /\bAvec\b/gi, replace: 'with' },
    { pattern: /\bSans\b/gi, replace: 'without' },
    { pattern: /\bRésistant\b/gi, replace: 'Durable' },
    { pattern: /\bImperméable\b/gi, replace: 'Waterproof' },
  ],
  fr: [
    { pattern: /\bCollar(s)?\b/gi, replace: (_m, plural) => plural ? 'Colliers' : 'Collier' },
    { pattern: /\bLeash(es)?\b/gi, replace: (_m, plural) => plural ? 'Laisses' : 'Laisse' },
    { pattern: /\bHarness(es)?\b/gi, replace: (_m, plural) => plural ? 'Harnais' : 'Harnais' },
    { pattern: /\bToy(s)?\b/gi, replace: (_m, plural) => plural ? 'Jouets' : 'Jouet' },
    { pattern: /\bTreat(s)?\b/gi, replace: (_m, plural) => plural ? 'Friandises' : 'Friandise' },
    { pattern: /\bPupp(y|ies)\b/gi, replace: (_m, type) => (type === 'y' ? 'Chiot' : 'Chiots') },
    { pattern: /\bDog(s)?\b/gi, replace: (_m, plural) => plural ? 'Chiens' : 'Chien' },
    { pattern: /\bKitten(s)?\b/gi, replace: (_m, plural) => plural ? 'Chatons' : 'Chaton' },
    { pattern: /\bCat(s)?\b/gi, replace: (_m, plural) => plural ? 'Chats' : 'Chat' },
    { pattern: /\bfor\b/gi, replace: 'pour' },
    { pattern: /\band\b/gi, replace: 'et' },
    { pattern: /\bSmall\b/gi, replace: 'Petit' },
    { pattern: /\bMedium\b/gi, replace: 'Moyen' },
    { pattern: /\bLarge\b/gi, replace: 'Grand' },
    { pattern: /\bBlack\b/gi, replace: 'Noir' },
    { pattern: /\bBlue\b/gi, replace: 'Bleu' },
    { pattern: /\bRed\b/gi, replace: 'Rouge' },
    { pattern: /\bGreen\b/gi, replace: 'Vert' },
    { pattern: /\bYellow\b/gi, replace: 'Jaune' },
    { pattern: /\bPink\b/gi, replace: 'Rose' },
    { pattern: /\bPurple\b/gi, replace: 'Violet' },
    { pattern: /\bGrey\b/gi, replace: 'Gris' },
    { pattern: /\bWhite\b/gi, replace: 'Blanc' },
    { pattern: /\bSet\b/gi, replace: 'Ensemble' },
    { pattern: /\bRefill\b/gi, replace: 'Recharge' },
    { pattern: /\bRange\b/gi, replace: 'Gamme' },
    { pattern: /\bwith\b/gi, replace: 'avec' },
    { pattern: /\bwithout\b/gi, replace: 'sans' },
    { pattern: /\bWaterproof\b/gi, replace: 'Imperméable' },
    { pattern: /\bDurable\b/gi, replace: 'Résistant' },
  ],
};

const ANIMAL_LABELS = {
  fr: {
    Chien: 'Chien',
    Chat: 'Chat',
    Chiot: 'Chiot',
    Chaton: 'Chaton',
    'Non spécifié': 'Non spécifié',
  },
  en: {
    Chien: 'Dog',
    Chat: 'Cat',
    Chiot: 'Puppy',
    Chaton: 'Kitten',
    'Non spécifié': 'Not specified',
  },
};

const CATEGORY_LABELS = {
  fr: {
    COLLIER: 'COLLIER',
    LAISSE: 'LAISSE',
    FRIANDISE: 'FRIANDISE',
    LITIERE: 'LITIERE',
    GAMELLE: 'GAMELLE',
    HARNAIS: 'HARNAIS',
    JOUET: 'JOUET',
    INTERACTIVE: 'INTERACTIF',
    HIVER: 'HIVER',
    EDUCATION: 'EDUCATION',
    COUCHAGE: 'COUCHAGE',
    HYGIENE: 'HYGIENE',
    GRIFFOIR: 'GRIFFOIR',
    TRANSPORT: 'TRANSPORT',
    ALIMENT: 'ALIMENT',
    ACCESSOIRE: 'ACCESSOIRE',
    SHAMPOOING: 'SHAMPOOING',
    'CAGE DE PROPRETÉ': 'CAGE DE PROPRETÉ',
    AQUATIQUE: 'AQUATIQUE',
    'ARBRE A CHAT': 'ARBRE A CHAT',
    'Non catégorisé': 'Non catégorisé',
  },
  en: {
    COLLIER: 'Collar',
    LAISSE: 'Leash',
    FRIANDISE: 'Treat',
    LITIERE: 'Litter',
    GAMELLE: 'Bowl',
    HARNAIS: 'Harness',
    JOUET: 'Toy',
    INTERACTIVE: 'INTERACTIVE',
    HIVER: 'Winter',
    EDUCATION: 'Training',
    COUCHAGE: 'Bedding',
    HYGIENE: 'Hygiene',
    GRIFFOIR: 'Scratcher',
    TRANSPORT: 'Transport',
    ALIMENT: 'Food',
    ACCESSOIRE: 'Accessory',
    SHAMPOOING: 'Shampoo',
    'CAGE DE PROPRETÉ': 'Housebreaking cage',
    AQUATIQUE: 'Aquatic',
    'ARBRE A CHAT': 'Cat tree',
    'Non catégorisé': 'Uncategorized',
  },
};

const resolvePath = (obj, path) => path.split('.').reduce(
  (acc, key) => (acc && Object.prototype.hasOwnProperty.call(acc, key) ? acc[key] : undefined),
  obj
);

// ---------- Utils parsing / nettoyage ----------
const get = (row, key) => row?.[key] ?? '';

const parseFirstImage = srcField => {
  const raw = String(srcField || '').split(/[\s,;]+/)[0] || '';
  return DOMPurify.sanitize(raw);
};
const parseBarcode = b => {
  const raw = b ? String(b).replace(/^'+/, '').trim() : '';
  return DOMPurify.sanitize(raw);
};
const parsePrice = p => {
  if (p === undefined || p === null || p === '') return '';
  const numStr = String(p).trim().replace(',', '.');
  return DOMPurify.sanitize('$' + numStr.replace('.', ','));
};
const parseInventoryQty = q => {
  const n = parseInt(q, 10);
  return Number.isNaN(n) ? 0 : n;
};
const sanitizeText = text => {
  if (!text) return '';
  const cleaned = String(text)
    .normalize('NFC')
    // eslint-disable-next-line
    .replace(/[\x00-\x1F\x7F-\x9F]/g, '')
    .replace(/\uFFFD/g, '')
    .replace(/Å½/g,      'é')
    .replace(/Ë†/g,      'à')
    .replace(/ï¿½/g,     'ê')
    .replace(/Ã©/g,      'é')
    .replace(/Ã¨/g,      'è')
    .replace(/Ã‰/g,      'É')
    .replace(/Ãˆ/g,      'È')
    .replace(/nÃuds/g,   'noeuds')
    .replace(/TRILÃ©NE/g,'TRILÈNE')
    .replace(/\s+/g, ' ')
    .trim();
  return DOMPurify.sanitize(cleaned);
};
const truncateText = (t, max = MAX_TITLE_LENGTH) =>
  t?.length <= max ? t : t.slice(0, max - 3).trimEnd() + '...';

// --- TOKENS depuis Tags Shopify ---
const tokenizeTags = (tagsStr = '') => {
  return new Set(
    String(tagsStr)
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => s.toUpperCase())
  );
};

/* =========================
   Découpage des BALISES
   (tout repose sur les Tags)
   ========================= */

// ANIMAUX via tags
const ANIMAL_TAGS_MAP = {
  CHIEN:  'Chien',
  CHIOT:  'Chiot',
  CHAT:   'Chat',
  CHATON: 'Chaton',
};
const DEFAULT_ANIMALS_ORDER = ['Chien', 'Chat', 'Chiot', 'Chaton', 'Non spécifié'];

// CATÉGORIES via tags (ordre de priorité de détection)
const CATEGORY_TAGS_ORDERED = [
  'COLLIER',
  'LAISSE',
  'FRIANDISE',
  'LITIERE',
  'GAMELLE',
  'HARNAIS',
  'JOUET',
  'INTERACTIVE',
  'HIVER',
  'EDUCATION',
  'COUCHAGE',
  'HYGIENE',
  'GRIFFOIR',
  'TRANSPORT',
  'ALIMENT',
  'ACCESSOIRE',
  'SHAMPOOING',
  'CAGE DE PROPRETÉ',
  'AQUATIQUE',
  'ARBRE A CHAT',
];

// Tags méta
const META_TAGS = new Set([
  'NOS PRODUITS',
  "PAGE D'ACCUEIL",
  'PRODUITS SANS TARIFS PREFERENTIELS',
]);

// --------- Déductions depuis les tags ----------
const deriveAnimalFromTags = (tagsSet) => {
  for (const key of Object.keys(ANIMAL_TAGS_MAP)) {
    if (tagsSet.has(key)) return ANIMAL_TAGS_MAP[key];
  }
  return 'Non spécifié';
};

const deriveCategoryFromTags = (tagsSet) => {
  // MAJ: Retrait du filtre 'BRAND_TAGS' qui n'existe plus
  const work = new Set([...tagsSet].filter(t => !META_TAGS.has(t)));
  for (const t of CATEGORY_TAGS_ORDERED) {
    if (work.has(t)) return t;
  }
  return 'Non catégorisé';
};

// Extraction PCB depuis la description (Body (HTML))
const extractPCB = (htmlOrText = '') => {
  const plain = String(htmlOrText).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const patterns = [
    /pcb[^0-9]{0,10}(\d{1,4})/i,
    /colisage[^0-9]{0,10}(\d{1,4})/i,
    /pack\s*(de)?\s*(\d{1,4})/i,
  ];
  for (const re of patterns) {
    const m = plain.match(re);
    if (m) {
      const n = parseInt(m[1] || m[2], 10);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
};

// Construit un label de variante
const buildVariantSizeLabel = (row) => {
  const v1 = get(row, 'Option1 Value');
  const v2 = get(row, 'Option2 Value');
  const v3 = get(row, 'Option3 Value');

  const vals = [v1, v2, v3]
    .map(v => String(v || '').trim())
    .filter(v => v && v !== 'Default Title');

  return vals.join(' - ').trim();
};

/* ========= Pricing helpers (UI + PDF) ========= */
const parseNumericPrice = (s) => {
  const m = String(s ?? '').match(/-?\d+(?:[.,]\d+)?/);
  const n = m ? parseFloat(m[0].replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : null;
};
const applyDiscountFmt = (priceString, discountPct) => {
  const n = parseNumericPrice(priceString);
  if (n == null) return priceString ?? '—';
  const factor = 1 - (Number(discountPct || 0) / 100);
  return `$${(n * factor).toFixed(2)}`;
};

// ---------- Composant ----------
const AdminCatalogPage = () => {
  const [language, setLanguage] = useState('fr');
  const t = useCallback((key, params = {}) => {
    const { fallback, ...rest } = params;
    const langPack = I18N[language] || I18N.fr;
    const value = resolvePath(langPack, key);
    if (typeof value === 'function') {
      return value(rest);
    }
    if (value !== undefined) return value;
    const fallbackValue = fallback ?? resolvePath(I18N.fr, key);
    if (typeof fallbackValue === 'function') {
      return fallbackValue(rest);
    }
    return fallbackValue ?? key;
  }, [language]);

  const translateTitle = useCallback((title) => {
    if (!title) return '';
    const replacements = TITLE_TRANSLATIONS[language];
    if (!replacements || !replacements.length) return title;
    return replacements.reduce((acc, { pattern, replace }) => {
      try {
        return acc.replace(pattern, replace);
      } catch {
        return acc;
      }
    }, title);
  }, [language]);

  const getLocalizedTitle = useCallback((title, { truncate = false, maxLength = MAX_TITLE_LENGTH } = {}) => {
    const translated = translateTitle(title);
    const sanitized = sanitizeText(translated);
    if (!truncate) return sanitized;
    return truncateText(sanitized, maxLength);
  }, [translateTitle]);

  const formatAnimal = useCallback((value) => {
    if (!value) return t('fallback.unknown');
    return ANIMAL_LABELS[language]?.[value]
      ?? ANIMAL_LABELS.fr?.[value]
      ?? value;
  }, [language, t]);

  const formatCategory = useCallback((value) => {
    if (!value) return t('fallback.unknown');
    return CATEGORY_LABELS[language]?.[value]
      ?? CATEGORY_LABELS.fr?.[value]
      ?? value;
  }, [language, t]);

  const getPriceModeText = useCallback(
    (mode) => t(`priceModes.labels.${mode}`),
    [t]
  );

  const getSortLabel = useCallback(
    (mode) => t(`sort.options.${mode}`, { fallback: mode }),
    [t]
  );

  const [products, setProducts]       = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Filtres & tri
  const [filterAnimal, setFilterAnimal]     = useState('Tous');
  const [filterCategory, setFilterCategory] = useState('Toutes');
  const [filterBrand, setFilterBrand]       = useState('Toutes');

  const [sortMode, setSortMode]               = useState('Aucun'); // 'Aucun' | 'Animaux' | 'Marques' | 'Catégories'
  const [customOrderList, setCustomOrderList] = useState([]);      // ordre personnalisé courant
  const [customExcludedList, setCustomExcludedList] = useState([]);// éléments exclus du tri courant

  // ===== Sélecteur de tarif =====
  const [priceMode, setPriceMode] = useState('REGULIER'); // REGULIER | MAGASIN | CHAINE | DISTRIBUTEUR | CUSTOM
  const [customDiscount, setCustomDiscount] = useState(0); // pour CUSTOM
  const discountFromMode = useMemo(() => {
    switch (priceMode) {
      case 'MAGASIN':       return 40;
      case 'CHAINE':        return 50;
      case 'DISTRIBUTEUR':  return 60;
      case 'CUSTOM':        return Math.max(0, Math.min(99.99, Number(customDiscount) || 0));
      case 'REGULIER':
      default:              return 0;
    }
  }, [priceMode, customDiscount]);
  const priceModeLabel = useMemo(() => {
    if (priceMode === 'CUSTOM') {
      return t('priceModes.customLabel', { discount: discountFromMode });
    }
    return getPriceModeText(priceMode) || t('priceModes.fallback');
  }, [priceMode, discountFromMode, getPriceModeText, t]);

  // ===== Cases à cocher (affichage) =====
  const [showPrices, setShowPrices] = useState(true);
  const [showQuantities, setShowQuantities] = useState(true);
  const [showPCB, setShowPCB] = useState(true); // <-- NOUVEAU

  // ===== Recherche =====
  const [searchQuery, setSearchQuery] = useState('');

  // UI modale tri
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [modalOrder, setModalOrder] = useState([]);       // inclus (réordonnables)
  const [modalExcluded, setModalExcluded] = useState([]); // exclus (réintégrables)
  const dragIndex = useRef(null);

  const renderSortValue = useCallback((value) => {
    if (!value) return t('fallback.unknown');
    if (sortMode === 'Animaux') return formatAnimal(value);
    if (sortMode === 'Catégories') return formatCategory(value);
    return value;
  }, [formatAnimal, formatCategory, sortMode, t]);

  // ------ Couverture / Page de fin (images) ------
  const [coverImage, setCoverImage] = useState(null); // { dataUrl, format, width, height }
  const [backImage, setBackImage] = useState(null);   // { dataUrl, format, width, height }
  const coverInputRef = useRef(null);
  const backInputRef = useRef(null);

  const readImageAsDataUrlWithDims = (file) =>
    new Promise((resolve, reject) => {
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        const img = new Image();
        img.onload = () => {
          const mime = (file.type || '').toLowerCase();
          const format = mime.includes('png') ? 'PNG' : 'JPEG';
          resolve({ dataUrl, format, width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = reject;
        img.src = dataUrl;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onPickCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const info = await readImageAsDataUrlWithDims(file);
    setCoverImage(info);
    e.target.value = '';
  };
  const onPickBack = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const info = await readImageAsDataUrlWithDims(file);
    setBackImage(info);
    e.target.value = '';
  };

  // Import CSV
  const handleFiles = async e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (files.some(f => f.size > MAX_CSV_SIZE)) {
      alert(t('alerts.fileTooLarge'));
      return;
    }

    try {
      const dataSets = await Promise.all(
        files.map(f =>
          new Promise((res, rej) =>
            Papa.parse(f, {
              header: true,
              skipEmptyLines: true,
              encoding: 'ISO-8859-1',
              complete: r => res(r.data),
              error: err => rej(err),
            })
          )
        )
      );
      const rows = dataSets.flat();

      const pre = rows.map(p => {
        const cleanTitle = sanitizeText(get(p, 'Title')?.trim() || '');
        const firstImage = parseFirstImage(get(p, 'Image Src'));
        const vendor = sanitizeText(get(p, 'Vendor')) || '';
        return {
          ...p,
          _TitleFull:  cleanTitle,
          _ShortTitle: truncateText(cleanTitle),
          _Vendor:     vendor || null,
          _Price:      parsePrice(get(p, 'Variant Price')),
          _Barcode:    parseBarcode(get(p, 'Variant Barcode')),
          _Qty:        parseInventoryQty(get(p, 'Variant Inventory Qty')),
          _FirstImage: firstImage,
          _TagsSet:    tokenizeTags(get(p, 'Tags')),
        };
      });

      const groups = new Map();
      for (const r of pre) {
        const handle = get(r, 'Handle') || r._TitleFull || get(r, 'SKU') || Math.random().toString(36).slice(2);
        if (!groups.has(handle)) groups.set(handle, []);
        groups.get(handle).push(r);
      }

      const grouped = [];
      for (const [handle, arr] of groups.entries()) {
        arr.sort((a, b) => {
          const pa = parseFloat(String(get(a, 'Variant Price')).replace(',', '.')) || 0;
          const pb = parseFloat(String(get(b, 'Variant Price')).replace(',', '.')) || 0;
          if (pa !== pb) return pa - pb;
          const la = buildVariantSizeLabel(a);
          const lb = buildVariantSizeLabel(b);
          return la.localeCompare(lb, 'fr');
        });

        const base = arr[0];

        const tagsUnion = new Set();
        for (const r of arr) r._TagsSet.forEach(t => tagsUnion.add(t));

        const titleFull   = base._TitleFull;
        const shortTitle  = base._ShortTitle;
        const vendor      = base._Vendor;
        const body        = get(base, 'Body (HTML)') || get(base, 'Body') || '';
        const pcb         = extractPCB(body);

        const firstImage  = (arr.find(r => r._FirstImage) || {})._FirstImage || '';

        const variantsRaw = arr.map(row => {
          const size = buildVariantSizeLabel(row);
          return {
            sizeLabel: size,
            price:     row._Price,
            qty:       row._Qty,
            barcode:   row._Barcode,
          };
        }).filter(v => v.sizeLabel);

        const variants = variantsRaw.length ? variantsRaw : [{
          sizeLabel: '',
          price: base._Price,
          qty:   base._Qty,
          barcode: base._Barcode,
        }];

        const animal    = deriveAnimalFromTags(tagsUnion);
        const category  = deriveCategoryFromTags(tagsUnion);

        // MAJ: Utiliser le champ 'Vendor' comme source pour la marque
        const brand     = vendor || '—';
        const brandsArr = vendor ? [vendor] : [];

        grouped.push({
          handle,
          TitleFull:  titleFull,
          ShortTitle: shortTitle,
          Vendor:     vendor,
          Category:   category,
          Animal:     animal,
          Brand:      brand, // Vient de 'Vendor'
          BrandList:  brandsArr, // Vient de 'Vendor'
          Tags:       Array.from(tagsUnion).join(', '),
          PCB:        pcb,
          firstImage,
          variants,
          firstVariant: variants[0],
        });
      }

      const cleaned = grouped.filter(p => p.TitleFull && p.firstImage);
      setProducts(cleaned);
      setCurrentPage(1);
    } catch (err) {
      console.error(t('alerts.parseErrorLog'), err);
      alert(t('alerts.csvError'));
    }
  };

  // ---------- Recherche ----------
  const filteredBySearch = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return products;
    const match = (p) => {
      const fields = [
        p.TitleFull, p.ShortTitle, p.Vendor, p.Category, p.Animal, p.Brand, p.Tags
      ].map(s => String(s || '').toLowerCase());
      if (fields.some(f => f.includes(q))) return true;
      if (Array.isArray(p.variants)) {
        for (const v of p.variants) {
          if (String(v.sizeLabel || '').toLowerCase().includes(q)) return true;
          if (String(v.barcode || '').toLowerCase().includes(q)) return true;
        }
      }
      return false;
    };
    return products.filter(match);
  }, [products, searchQuery]);

  // ---------- Application des filtres de base ----------
  const filteredBase = useMemo(() => {
    return filteredBySearch.filter(p => {
      if (filterAnimal !== 'Tous' && p.Animal !== filterAnimal) return false;
      if (filterCategory !== 'Toutes' && p.Category !== filterCategory) return false;
      if (filterBrand !== 'Toutes') {
        const hasBrand = (p.BrandList && p.BrandList.includes(filterBrand)) || p.Brand === filterBrand;
        if (!hasBrand) return false;
      }
      return true;
    });
  }, [filteredBySearch, filterAnimal, filterCategory, filterBrand]);

  // ---------- Application des exclusions du tri courant ----------
  const filtered = useMemo(() => {
    if (sortMode === 'Aucun' || !customExcludedList.length) return filteredBase;

    const exclSetLower = new Set(customExcludedList.map(s => String(s).toLowerCase()));
    const getGroupKey = (p) => {
      if (sortMode === 'Animaux') return p.Animal;
      if (sortMode === 'Marques') return (p.Brand !== '—' ? p.Brand : (p.BrandList?.[0] || ''));
      if (sortMode === 'Catégories') return p.Category;
      return '';
    };

    return filteredBase.filter(p => !exclSetLower.has(String(getGroupKey(p)).toLowerCase()));
  }, [filteredBase, sortMode, customExcludedList]);

  // ---------- Tri avec ordre personnalisé ----------
  const customOrderArray = customOrderList;

  const sorted = useMemo(() => {
    const arr = [...filtered];

    const getGroupKey = (p) => {
      if (sortMode === 'Animaux') return p.Animal;
      if (sortMode === 'Marques') return (p.Brand !== '—' ? p.Brand : (p.BrandList?.[0] || ''));
      if (sortMode === 'Catégories') return p.Category;
      return '';
    };

    const rankOf = (key) => {
      const idx = customOrderArray.findIndex(x => x.toLowerCase() === String(key || '').toLowerCase());
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    if (sortMode !== 'Aucun') {
      arr.sort((a, b) => {
        const ga = getGroupKey(a);
        const gb = getGroupKey(b);
        const ra = rankOf(ga);
        const rb = rankOf(gb);

        if (ra !== rb) return ra - rb;
        const gcmp = String(ga).localeCompare(String(gb), 'fr');
        if (gcmp !== 0) return gcmp;
        return a.TitleFull.localeCompare(b.TitleFull, 'fr');
      });
    }

    return arr;
  }, [filtered, sortMode, customOrderArray]);

  // Pagination
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE) || 1;
  const pageItems  = sorted.slice((currentPage - 1) * PAGE_SIZE, (currentPage - 1) * PAGE_SIZE + PAGE_SIZE);

  // ---------- Images pour PDF : JPEG compressé + alias ----------
  const getImageDataUrl = (url, quality = 0.72) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c   = document.createElement('canvas');
      c.width   = MAX_IMG_DIM;
      c.height  = MAX_IMG_DIM;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, MAX_IMG_DIM, MAX_IMG_DIM);
      const scale = Math.min(MAX_IMG_DIM / img.width, MAX_IMG_DIM / img.height);
      const dw    = img.width * scale;
      const dh    = img.height * scale;
      const dx    = (MAX_IMG_DIM - dw) / 2;
      const dy    = (MAX_IMG_DIM - dh) / 2;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, img.width, img.height, dx, dy, dw, dh);
      // JPEG (qualité ajustable) -> gain de poids majeur
      resolve(c.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => reject(new Error(`Impossible de charger l’image : ${url}`));
    img.src = url;
  });

  const preloadImages = items => Promise.all(
    items.map(async p => {
      try { return { ...p, imageData: await getImageDataUrl(p.firstImage) }; }
      catch { return { ...p, imageData: null }; }
    })
  );

  // ---------- Helpers cover/back ----------
  const drawFullPageImage = (doc, imgObj) => {
    if (!imgObj?.dataUrl) return;
    const { width: pageW, height: pageH } = doc.internal.pageSize;
    // fond blanc
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageW, pageH, 'F');

    const iw = imgObj.width || pageW;
    const ih = imgObj.height || pageH;
    const scale = Math.min(pageW / iw, pageH / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = (pageW - dw) / 2;
    const dy = (pageH - dh) / 2;

    doc.addImage(imgObj.dataUrl, imgObj.format || 'JPEG', dx, dy, dw, dh, undefined, 'FAST');
  };

  // ---------- Génération PDF (PAGE AFFICHÉE) ----------
  const downloadPDF = async () => {
    if (!sorted.length) return;

    const items = await preloadImages(sorted);

    // Compression activée, précision flottante haute, seulement les fontes utilisées
    const doc   = new jsPDF({
      orientation: 'p',
      unit: 'pt',
      format: 'a4',
      compress: true,
      putOnlyUsedFonts: true,
      floatPrecision: 16
    });
    const { width: pageW, height: pageH } = doc.internal.pageSize;

    const margin   = 20;
    const cols     = 2;
    const rows     = 3;
    const gap      = 10;
    const cellW    = (pageW - 2*margin - (cols - 1)*gap) / cols;
    const cellH    = (pageH - 2*margin - (rows - 1)*gap) / rows;

    const imgSize     = cellH * 0.48;
    const lineH       = 11;

    const varBoxH     = 64;
    const varBoxPadX  = 6;
    const varBoxPadY  = 6;

    const pickBarcodeFormat = (value = '') => {
      const v = String(value).trim();
      return /^\d{12,13}$/.test(v) ? 'EAN13' : 'CODE128';
    };

    // ===== Page de couverture (si fournie) =====
    if (coverImage?.dataUrl) {
      drawFullPageImage(doc, coverImage);
    }

    // Alias d'images : évite d’embarquer plusieurs fois la même image
    const imageAliasMap = new Map(); // url -> alias

    const pagesNeeded = Math.ceil(items.length / PAGE_SIZE);
    for (let pIdx = 0; pIdx < pagesNeeded; pIdx++) {
      if (pIdx > 0 || coverImage?.dataUrl) doc.addPage();
      const slice = items.slice(pIdx * PAGE_SIZE, pIdx * PAGE_SIZE + PAGE_SIZE);

      slice.forEach((prod, idx) => {
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        const x0  = margin + col * (cellW + gap);
        const y0  = margin + row * (cellH + gap);

        // Image produit (JPEG compressé)
        const xImg = x0 + (cellW - imgSize) / 2;
        if (prod.imageData) {
          let alias = imageAliasMap.get(prod.firstImage);
          if (!alias) {
            alias = `img_${imageAliasMap.size + 1}`;
            imageAliasMap.set(prod.firstImage, alias);
          }
          // format 'JPEG' + compression (jsPDF réutilise l'alias si déjà vu)
          doc.addImage(prod.imageData, 'JPEG', xImg, y0, imgSize, imgSize, alias, 'MEDIUM');
        } else {
          doc.setDrawColor(200);
          doc.rect(xImg, y0, imgSize, imgSize);
        }

        // Métadonnées
        let ty = y0 + imgSize + lineH;
        doc.setFontSize(10);

        const titleLine   = getLocalizedTitle(prod.TitleFull, { truncate: true });
        const vendorText  = prod.Vendor ?? t('fallback.unknownVendor');
        const metaVendor  = sanitizeText(`${t('pdf.vendor')} ${vendorText}`);
        const metaAnimal  = sanitizeText(`${t('pdf.animal')} ${formatAnimal(prod.Animal)}`);
        const metaCat     = sanitizeText(`${t('pdf.category')} ${formatCategory(prod.Category)}`);
        const metaPCB     = sanitizeText(t('pdf.pcb', { value: prod.PCB ?? null }));

        const metaLines = [titleLine, metaVendor, metaAnimal, metaCat];
        if (showPCB) metaLines.push(metaPCB); // toggle PCB

        metaLines.forEach(txt => {
          doc.text(txt, x0, ty, { maxWidth: cellW });
          ty += lineH;
        });

        // ======= Carré compact =======
        const fv   = prod.firstVariant;
        const boxX = x0;
        const boxY = ty + 2;
        const boxW = cellW;
        const boxH = varBoxH;

        doc.setFillColor(250, 250, 250);
        doc.setDrawColor(229, 231, 235);
        if (typeof doc.roundedRect === 'function') {
          doc.roundedRect(boxX, boxY, boxW, boxH, 4, 4, 'FD');
        } else {
          doc.rect(boxX, boxY, boxW, boxH, 'FD');
        }

        // Contenu
        let bx = boxX + varBoxPadX;
        let by = boxY + varBoxPadY;

        // Ligne 1 : titre
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        const headerLine = showPrices
          ? sanitizeText(priceModeLabel)
          : sanitizeText(fv?.sizeLabel || '');
        doc.text(headerLine, bx, by + 9, { maxWidth: boxW - 10 });

        // Ligne 2 : prix (sous le titre, à gauche)
        if (showPrices) {
          doc.setFont(undefined, 'normal');
          doc.setFontSize(9);
          by += 14;
          doc.text(
            sanitizeText(fv?.price ? applyDiscountFmt(fv.price, discountFromMode) : t('fallback.unknown')),
            bx,
            by + 9,
            { maxWidth: boxW - 10 }
          );
        }

        // Ligne 2 : Qté (si affichage qté)
        by += 16;
        if (showQuantities) {
          doc.setFontSize(8.5);
          doc.text(sanitizeText(`${t('pdf.quantity')} ${fv?.qty ?? 0}`), bx, by + 7);
        }

        // Code-barres
        if (fv?.barcode) {
          const bcWpt = 96;
          const bcHpt = 28;
          const format = pickBarcodeFormat(fv.barcode);

          const scale  = 4;
          const canvas = document.createElement('canvas');
          canvas.width  = Math.round(bcWpt * scale);
          canvas.height = Math.round(bcHpt * scale);

          const ctx = canvas.getContext('2d');
          if (ctx) ctx.imageSmoothingEnabled = false;

          try {
            JsBarcode(canvas, fv.barcode, {
              format,
              lineColor: '#000',
              background: 'rgba(0,0,0,0)',
              width:  0.55 * scale,
              height: Math.round((bcHpt - 10) * scale),
              displayValue: true,
              fontSize: 9 * scale,
              textMargin: 2 * scale,
              margin: 0
            });

            const bcDataUrl = canvas.toDataURL('image/png');
            const bcX = boxX + boxW - varBoxPadX - bcWpt;
            const bcY = boxY + (boxH - bcHpt) / 2;
            doc.addImage(bcDataUrl, 'PNG', bcX, bcY, bcWpt, bcHpt, undefined, 'FAST');
          } catch {}
        }

        // Indicateur “+n autres variantes”
        ty = boxY + boxH + 8;
        if (Array.isArray(prod.variants) && prod.variants.length > 1) {
          const rest = prod.variants.length - 1;
          doc.setFontSize(8);
          doc.text(sanitizeText(t('pdf.additionalVariants', { rest })), x0, ty, { maxWidth: cellW });
        }
      });
    }

    // ===== Page de fin (si fournie) =====
    if (backImage?.dataUrl) {
      doc.addPage();
      drawFullPageImage(doc, backImage);
    }

    const blob = doc.output('blob');
    const link = document.createElement('a');
    link.href     = URL.createObjectURL(blob);
    link.download = t('general.pdfFileName');
    link.click();
  };

  const commitOrderFromModal = () => {
    setCustomOrderList(modalOrder);
    setCustomExcludedList(modalExcluded);
    setIsOrderModalOpen(false);
    setCurrentPage(1);
  };

  const cancelOrderModal = () => {
    setIsOrderModalOpen(false);
  };

  // drag & drop handlers (HTML5)
  const onDragStart = (index) => (e) => {
    dragIndex.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (overIndex) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (dropIndex) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === undefined) return;
    if (from === dropIndex) return;
    setModalOrder(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    dragIndex.current = null;
  };

  // exclude / include buttons in modal
  const excludeAtIndex = (idx) => {
    setModalExcluded(prev => [...prev, modalOrder[idx]]);
    setModalOrder(prev => prev.filter((_, i) => i !== idx));
  };
  const includeKey = (key) => {
    setModalOrder(prev => [...prev, key]);
    setModalExcluded(prev => prev.filter(k => k !== key));
  };

  // ---------- Rendu ----------
  return (
    <div className="dashboard-container">
      <AdminNavbar />
      <style>{`
        .acat-page { width: 100%; max-width: var(--eb-content-max); display: flex; flex-direction: column; }
        .dashboard-container .acat-page .eb-page-title { margin: 0; text-align: left; }
        .acat-page label { font-size: 13.5px; color: var(--eb-text-2); }
        .acat-page select, .acat-page input[type="number"], .acat-page input[type="text"] { min-height: 40px; padding: 0 10px !important; border-radius: var(--eb-radius-sm); font-size: 14px; background: var(--eb-surface); }
        .acat-page input[type="file"] { font-size: 14px; color: var(--eb-text-2); padding: 14px; width: 100%; background: var(--eb-surface); border: 1px dashed var(--eb-border-strong); border-radius: var(--eb-radius); }
        .acat-page input[type="file"]::file-selector-button { margin-right: 12px; min-height: 36px; padding: 0 14px; border-radius: var(--eb-radius-sm); border: 1px solid var(--eb-border-strong); background: var(--eb-surface); color: var(--eb-text-2); font-weight: 600; cursor: pointer; }
        .acat-page input[type="file"]::file-selector-button:hover { background: var(--eb-surface-2); }
        .acat-page .eb-search input[type="text"] { padding-left: 40px !important; }
        .acat-filters { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; align-items: end; margin-bottom: 16px; padding: 16px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-xs); }
        .acat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; margin-top: 8px; }
        .acat-card { border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); padding: 14px; text-align: left; background: var(--eb-surface); box-shadow: var(--eb-shadow-xs); min-height: 360px; display: flex; flex-direction: column; min-width: 0; }
        .dashboard-container .acat-card h3 { font-size: 15px; text-align: left; font-weight: 650; }
        .acat-lang { border: 1px solid var(--eb-border); background: var(--eb-surface); color: var(--eb-text-2); border-radius: 999px; padding: 6px 10px; display: flex; align-items: center; gap: 6px; cursor: pointer; font-weight: 600; transition: background .15s ease; }
        .acat-lang.is-active { background: var(--eb-brand); border-color: var(--eb-brand); color: #fff; }
        @media (max-width: 600px) {
          .acat-head-right { align-items: stretch !important; min-width: 0 !important; width: 100%; }
          .acat-head-right > div { flex-wrap: wrap; }
        }
      `}</style>
      <div className="acat-page">
        <div className="eb-page-header" style={{ alignItems:'flex-start', marginBottom: 16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <h1 className="eb-page-title">
              {t('general.generateCatalogTitle')}
            </h1>
            <div style={{ display:'flex', gap:8 }}>
              {AVAILABLE_LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setLanguage(lang)}
                  aria-pressed={language === lang}
                  title={I18N[lang]?.languageName}
                  className={`acat-lang${language === lang ? ' is-active' : ''}`}
                >
                  <span role="img" aria-label={I18N[lang]?.languageName} style={{ fontSize:'1.1rem', lineHeight:1 }}>
                    {I18N[lang]?.flag}
                  </span>
                  <span style={{ fontSize:12.5 }}>{lang.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Bloc tarif + cases à cocher */}
          {products.length > 0 && (
            <div className="acat-head-right" style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8, minWidth:260 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <label style={{ fontWeight:600 }}>{t('general.priceLabel')}</label>
                <select
                  value={priceMode}
                  onChange={(e) => setPriceMode(e.target.value)}
                  style={{ padding:'8px' }}
                  title={t('priceModes.selectTitle')}
                >
                  <option value="REGULIER">{getPriceModeText('REGULIER')}</option>
                  <option value="MAGASIN">{getPriceModeText('MAGASIN')}</option>
                  <option value="CHAINE">{getPriceModeText('CHAINE')}</option>
                  <option value="DISTRIBUTEUR">{getPriceModeText('DISTRIBUTEUR')}</option>
                  <option value="CUSTOM">{t('priceModes.customOption')}</option>
                </select>
                {priceMode === 'CUSTOM' && (
                  <>
                    <input
                      type="number"
                      min="0"
                      max="99.99"
                      step="0.01"
                      value={customDiscount}
                      onChange={(e)=> setCustomDiscount(e.target.value)}
                      style={{ width:90, padding:'8px' }}
                      title={t('priceModes.customDiscountTitle')}
                    />
                    <span>%</span>
                  </>
                )}
              </div>

              {/* Cases à cocher */}
              <div style={{ display:'flex', gap:16, fontSize:13.5, color:'var(--eb-text-2)', flexWrap:'wrap' }}>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showPrices}
                    onChange={(e)=> setShowPrices(e.target.checked)}
                    title={t('toggles.showPricesTitle')}
                  />
                  {t('toggles.showPrices')}
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showQuantities}
                    onChange={(e)=> setShowQuantities(e.target.checked)}
                    title={t('toggles.showQuantitiesTitle')}
                  />
                  {t('toggles.showQuantities')}
                </label>
                {/* Nouveau toggle PCB */}
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showPCB}
                    onChange={(e)=> setShowPCB(e.target.checked)}
                    title={t('toggles.showPCBTitle')}
                  />
                  {t('toggles.showPCB')}
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Import CSV */}
        <input
          type="file"
          accept=".csv"
          multiple
          onChange={handleFiles}
          style={{ margin:'0 0 16px' }}
        />

        {/* Barre de recherche (au-dessus des filtres/tri) */}
        {products.length > 0 && (
          <div className="eb-search" style={{ marginBottom:'12px' }}>
            <FaSearch className="eb-search__icon" aria-hidden="true" />
            <input
              type="text"
              className="eb-input"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e)=> { setSearchQuery(e.target.value); setCurrentPage(1); }}
              style={{ width:'100%', minHeight:44 }}
            />
          </div>
        )}

        {/* Panneau filtres + tri (sur une seule ligne) */}
        {products.length > 0 && (
          <div className="acat-filters">
            {/* Filtrer par animal */}
            <div>
              <label style={{ fontWeight:600, display:'block', marginBottom:6 }}>{t('filters.animalLabel')}</label>
              <select
                value={filterAnimal}
                onChange={(e)=>{ setFilterAnimal(e.target.value); setCurrentPage(1); }}
                style={{ width:'100%', padding:'8px' }}
              >
                {(['Tous', ...Array.from(new Set(products.map(p => p.Animal).filter(Boolean))).sort((a,b)=>a.localeCompare(b,'fr'))]).map(a => (
                  <option key={a} value={a}>
                    {a === 'Tous' ? t('filters.allAnimals') : formatAnimal(a)}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtrer par catégorie */}
            <div>
              <label style={{ fontWeight:600, display:'block', marginBottom:6 }}>{t('filters.categoryLabel')}</label>
              <select
                value={filterCategory}
                onChange={(e)=>{ setFilterCategory(e.target.value); setCurrentPage(1); }}
                style={{ width:'100%', padding:'8px' }}
              >
                {(['Toutes', ...Array.from(new Set(products.map(p => p.Category).filter(Boolean))).sort((a,b)=>a.localeCompare(b,'fr'))]).map(c => (
                  <option key={c} value={c}>
                    {c === 'Toutes' ? t('filters.allCategories') : formatCategory(c)}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtrer par marque */}
            <div>
              <label style={{ fontWeight:600, display:'block', marginBottom:6 }}>{t('filters.brandLabel')}</label>
              <select
                value={filterBrand}
                onChange={(e)=>{ setFilterBrand(e.target.value); setCurrentPage(1); }}
                style={{ width:'100%', padding:'8px' }}
              >
                {(['Toutes', ...Array.from(new Set(
                  products.flatMap(p => (p.BrandList && p.BrandList.length ? p.BrandList : (p.Brand !== '—' ? [p.Brand] : [])))
                )).sort((a,b)=>a.localeCompare(b,'fr'))]).map(b => (
                  <option key={b} value={b}>
                    {b === 'Toutes' ? t('filters.allBrands') : b}
                  </option>
                ))}
              </select>
            </div>

            {/* Tri + bouton ordre personnalisé */}
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <label style={{ fontWeight:600 }}>{t('sort.label')}</label>
                <button
                  onClick={()=>{
                    if (sortMode==='Aucun') return;
                    const available = (() => {
                      const set = new Set();
                      if (sortMode === 'Animaux') {
                        filteredBase.forEach(p => set.add(p.Animal));
                      } else if (sortMode === 'Marques') {
                        filteredBase.forEach(p => {
                          if (p.BrandList?.length) p.BrandList.forEach(b => set.add(b));
                          else if (p.Brand !== '—') set.add(p.Brand);
                        });
                      } else if (sortMode === 'Catégories') {
                        filteredBase.forEach(p => set.add(p.Category));
                      }
                      const arr = Array.from(set);
                      if (arr.length === 0) {
                        if (sortMode === 'Animaux') return DEFAULT_ANIMALS_ORDER;
                        // MAJ: Retrait de BRAND_TAGS
                        if (sortMode === 'Catégories') return [...CATEGORY_TAGS_ORDERED];
                      }
                      return arr.sort((a,b)=>String(a).localeCompare(String(b),'fr'));
                    })();

                    const currentOrder = customOrderList.length
                      ? customOrderList
                      : (sortMode === 'Animaux' ? DEFAULT_ANIMALS_ORDER
                        // MAJ: Retrait de BRAND_TAGS
                        : sortMode === 'Catégories' ? [...CATEGORY_TAGS_ORDERED] : []);

                    const included = [
                      ...currentOrder.filter(k => available.includes(k)),
                      ...available.filter(k => !currentOrder.includes(k))
                    ];
                    const currentExcluded = customExcludedList.filter(k => available.includes(k));
                    const excluded = [
                      ...currentExcluded,
                      ...available.filter(k => !included.includes(k) && !currentExcluded.includes(k))
                    ];

                    setModalOrder(included);
                    setModalExcluded(excluded);
                    setIsOrderModalOpen(true);
                  }}
                  disabled={sortMode === 'Aucun'}
                  title={sortMode === 'Aucun' ? t('sort.buttonTitle.disabled') : t('sort.buttonTitle.enabled')}
                  style={{
                    border:'1px solid var(--eb-border-strong)',
                    background:'var(--eb-surface)',
                    padding:'6px 8px',
                    borderRadius:8,
                    cursor: sortMode === 'Aucun' ? 'not-allowed' : 'pointer',
                    opacity: sortMode === 'Aucun' ? 0.5 : 1,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4 10h16M4 14h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
              <select
                value={sortMode}
                onChange={(e)=>{
                  setSortMode(e.target.value);
                  setCustomOrderList([]);
                  setCustomExcludedList([]);
                  setCurrentPage(1);
                }}
                style={{ width:'100%', padding:'8px' }}
              >
                {['Aucun', 'Animaux', 'Marques', 'Catégories'].map(mode => (
                  <option key={mode} value={mode}>{getSortLabel(mode)}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Grille produits */}
        {products.length > 0 && (
          <>
            <div className="acat-grid">
              {pageItems.map((p) => {
                const localizedFullTitle = getLocalizedTitle(p.TitleFull);
                const localizedShortTitle = getLocalizedTitle(p.TitleFull, { truncate: true });
                return (
                  <div
                    key={p.handle}
                    className="acat-card"
                    title={localizedFullTitle}
                  >
                    <div style={{
                      height:'200px',
                      display:'flex',
                      alignItems:'center',
                      justifyContent:'center',
                      marginBottom:'8px'
                    }}>
                      <img
                        src={p.firstImage}
                        alt={localizedShortTitle}
                        style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }}
                        onError={e => (e.currentTarget.style.opacity = 0.3)}
                      />
                    </div>

                    <h3 style={{ margin:'6px 0 4px' }}>{localizedShortTitle}</h3>

                    <div style={{ fontSize:13, color:'var(--eb-text-2)', marginBottom:4 }}>
                      <strong>{t('cards.supplier')}</strong> {p.Vendor ?? t('fallback.unknownVendor')}
                    </div>

                    <div style={{ fontSize:13, color:'var(--eb-text-2)', marginBottom:2 }}>
                      <strong>{t('cards.animal')}</strong> {formatAnimal(p.Animal)}
                    </div>
                    <div style={{ fontSize:13, color:'var(--eb-text-2)', marginBottom:8 }}>
                      <strong>{t('cards.category')}</strong> {formatCategory(p.Category)}
                    </div>

                    {showPCB && (
                      <div style={{ fontSize:13, color:'var(--eb-text-2)', marginBottom:8 }}>
                        <strong>{t('cards.pcb')}</strong> {p.PCB ?? t('fallback.unknown')}
                      </div>
                    )}

                    <div style={{ display:'grid', gridTemplateColumns:'1fr', rowGap:'6px', marginTop:'4px' }}>
                      {p.variants.map((v, idx) => (
                        <div key={idx} style={{
                          border:'1px solid var(--eb-border)',
                          borderRadius:'8px',
                          padding:'6px 10px',
                          fontSize:12,
                          background:'var(--eb-surface-2)',
                        }}>
                          {/* header du carré gris : tarif OU fallback variante */}
                          <div style={{ fontWeight:600 }}>
                            {showPrices ? (priceModeLabel) : (v.sizeLabel || '')}
                          </div>
                          {showPrices && (
                            <div>{t('cards.pricePurchase')} {v.price ? applyDiscountFmt(v.price, discountFromMode) : t('fallback.unknown')}</div>
                          )}
                          {showQuantities && (
                            <div>{t('cards.quantity')} {v.qty}</div>
                          )}
                          {v.barcode ? <div>{t('cards.barcode')} {v.barcode}</div> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div style={{ marginTop:'20px', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="eb-btn eb-btn--secondary"
              >
                {t('pagination.prev')}
              </button>
              <span style={{ margin:'0 8px', fontSize:14, color:'var(--eb-muted)' }}>
                {t('pagination.status', { current: currentPage, total: totalPages })}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="eb-btn eb-btn--secondary"
              >
                {t('pagination.next')}
              </button>
            </div>

            {/* ----- Boutons Couverture / Fin + PDF ----- */}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:24, paddingTop:20, borderTop:'1px solid var(--eb-border)', alignItems:'center' }}>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display:'none' }}
                onChange={onPickCover}
              />
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                className="eb-btn eb-btn--secondary"
              >
                {t('general.chooseCover')}
              </button>
              {coverImage && (
                <span style={{ fontSize:12, color:'var(--eb-success)' }}>
                  • {t('general.coverChosen')}
                  <button
                    onClick={()=>setCoverImage(null)}
                    style={{ marginLeft:8, fontSize:12, background:'transparent', border:'none', color:'var(--eb-danger)', cursor:'pointer' }}
                  >
                    ({t('general.clear')})
                  </button>
                </span>
              )}

              <input
                ref={backInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display:'none' }}
                onChange={onPickBack}
              />
              <button
                type="button"
                onClick={() => backInputRef.current?.click()}
                className="eb-btn eb-btn--secondary"
              >
                {t('general.chooseBack')}
              </button>
              {backImage && (
                <span style={{ fontSize:12, color:'var(--eb-success)' }}>
                  • {t('general.backChosen')}
                  <button
                    onClick={()=>setBackImage(null)}
                    style={{ marginLeft:8, fontSize:12, background:'transparent', border:'none', color:'var(--eb-danger)', cursor:'pointer' }}
                  >
                    ({t('general.clear')})
                  </button>
                </span>
              )}

              <button
                onClick={downloadPDF}
                className="eb-btn eb-btn--primary"
                style={{ marginLeft:'auto' }}
              >
                {t('general.downloadPdf')}
              </button>
            </div>
          </>
        )}

        {/* -------- Modale d’ordre personnalisé + exclusions -------- */}
        {isOrderModalOpen && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position:'fixed', inset:0, background:'rgba(15,35,56,.55)', backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)', padding:16,
              display:'flex', alignItems:'center', justifyContent:'center', zIndex: 9999
            }}
            onClick={cancelOrderModal}
          >
            <div
              onClick={(e)=>e.stopPropagation()}
              style={{
                width:'100%', maxWidth:560,
                maxHeight:'86vh',
                overflow:'hidden',
                background:'var(--eb-surface)',
                borderRadius:'var(--eb-radius-xl)',
                boxShadow:'var(--eb-shadow-lg)',
                display:'flex',
                flexDirection:'column'
              }}
            >
              <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--eb-border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ fontWeight:650, fontSize:17 }}>
                  {t('modal.title', { mode: getSortLabel(sortMode) })}
                </div>
                <button
                  onClick={cancelOrderModal}
                  style={{ border:'none', background:'transparent', cursor:'pointer', padding:6 }}
                  title={t('modal.closeTitle')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              <div style={{ padding:'12px 16px', overflow:'auto' }}>
                <p style={{ margin:'0 0 10px', color:'var(--eb-text-2)' }}>{t('modal.instructions')}</p>

                {/* Inclus (réordonnables) */}
                <h4 style={{ margin:'8px 0 10px' }}>{t('modal.included')}</h4>
                {modalOrder.length === 0 && (
                  <div style={{ fontSize:13, color:'var(--eb-muted)', marginBottom:10 }}>{t('modal.emptyIncluded')}</div>
                )}
                <ul style={{ listStyle:'none', padding:0, margin:0 }}>
                  {modalOrder.map((item, index) => (
                    <li
                      key={`inc-${item}`}
                      draggable
                      onDragStart={onDragStart(index)}
                      onDragOver={onDragOver(index)}
                      onDrop={onDrop(index)}
                      style={{
                        display:'flex',
                        alignItems:'center',
                        gap:10,
                        padding:'10px 12px',
                        border:'1px solid var(--eb-border)',
                        borderRadius:10,
                        marginBottom:8,
                        background:'var(--eb-surface-2)',
                        cursor:'grab',
                        userSelect:'none'
                      }}
                      title={t('modal.dragTitle')}
                    >
                      <span style={{
                        display:'inline-flex',
                        alignItems:'center',
                        justifyContent:'center',
                        width:26, height:26,
                        borderRadius:8,
                        border:'1px solid var(--eb-border)',
                        background:'var(--eb-surface)',
                        fontSize:12, fontWeight:700
                      }}>
                        {index + 1}
                      </span>
                      <span style={{ flex:1, fontWeight:600, textTransform:'none' }}>{renderSortValue(item)}</span>

                      {/* bouton EXCLURE (–) en vert */}
                      <button
                        onClick={() => excludeAtIndex(index)}
                        title={t('modal.excludeTitle')}
                        style={{
                          border:'1px solid #c6f0da',
                          background:'var(--eb-success-bg)',
                          color:'var(--eb-success)',
                          padding:'6px 10px',
                          borderRadius:8,
                          cursor:'pointer',
                          fontWeight:800,
                          lineHeight:1
                        }}
                      >
                        –
                      </button>

                      {/* poignée */}
                      <span aria-hidden="true" style={{ opacity:.6 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M4 10h16M4 14h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Exclus (réintégrables) */}
                <h4 style={{ margin:'16px 0 10px' }}>{t('modal.excluded')}</h4>
                {modalExcluded.length === 0 && (
                  <div style={{ fontSize:13, color:'var(--eb-muted)' }}>{t('modal.emptyExcluded')}</div>
                )}
                <ul style={{ listStyle:'none', padding:0, margin:0 }}>
                  {modalExcluded.map((item) => (
                    <li
                      key={`exc-${item}`}
                      style={{
                        display:'flex',
                        alignItems:'center',
                        gap:10,
                        padding:'10px 12px',
                        border:'1px dashed #fbd0cb',
                        borderRadius:10,
                        marginBottom:8,
                        background:'var(--eb-danger-bg)'
                      }}
                    >
                      <span style={{ flex:1, fontWeight:600 }}>{renderSortValue(item)}</span>
                      {/* bouton INCLURE (+) en rouge */}
                      <button
                        onClick={() => includeKey(item)}
                        title={t('modal.includeTitle')}
                        style={{
                          border:'1px solid #fbd0cb',
                          background:'var(--eb-danger-bg)',
                          color:'var(--eb-danger)',
                          padding:'6px 10px',
                          borderRadius:8,
                          cursor:'pointer',
                          fontWeight:800,
                          lineHeight:1
                        }}
                      >
                        +
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{ padding:'12px 16px', borderTop:'1px solid var(--eb-border)', display:'flex', justifyContent:'flex-end', gap:10 }}>
                <button
                  onClick={cancelOrderModal}
                  className="eb-btn eb-btn--secondary"
                >
                  {t('modal.cancel')}
                </button>
                <button
                  onClick={commitOrderFromModal}
                  className="eb-btn eb-btn--primary"
                >
                  {t('modal.apply')}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* -------- Fin modale -------- */}
      </div>

      {/* Inputs fichiers (cachés) global bas pour éviter héritages CSS */}
      <input ref={coverInputRef} type="file" accept="image/png,image/jpeg" style={{ display:'none' }} onChange={onPickCover} />
      <input ref={backInputRef} type="file" accept="image/png,image/jpeg" style={{ display:'none' }} onChange={onPickBack} />
    </div>
  );
};

export default AdminCatalogPage;